import { useCallback } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

/**
 * Custom hook for CSV and XLSX export functionality
 * Provides reusable export generation and download capabilities
 */
export const useCSVExport = () => {
  /**
   * Convert data to CSV format
   * @param {Array} data - Array of objects to convert
   * @param {Array} fields - Array of field names/headers
   * @param {Function} dataMapper - Optional function to transform data
   * @returns {string} CSV content string
   */
  const generateCSV = useCallback((data, fields, dataMapper = null) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return "";
    }

    // Transform data if mapper function is provided
    const processedData = dataMapper
      ? data.map((item, index) => dataMapper(item, index))
      : data;

    // Create CSV content
    const csvContent = [
      fields.join(","), // Header row
      ...processedData.map((row, index) =>
        fields
          .map((field) => {
            let value = row[field];

            // Handle undefined/null values
            if (value === undefined || value === null) {
              value = "";
            }

            // Convert to string
            value = value.toString();

            // Handle values that might contain commas, quotes, or newlines
            if (
              value.includes(",") ||
              value.includes('"') ||
              value.includes("\n")
            ) {
              // Escape quotes by doubling them and wrap in quotes
              value = `"${value.replace(/"/g, '""')}"`;
            }

            return value;
          })
          .join(","),
      ),
    ].join("\n");

    return csvContent;
  }, []);

  /**
   * Download CSV file
   * @param {string} csvContent - CSV content string
   * @param {string} filename - Filename for download (without extension)
   */
  const downloadCSV = useCallback((csvContent, filename = "export") => {
    if (!csvContent) {
      console.warn("No CSV content provided for download");
      return;
    }

    try {
      // Create blob with CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      // Create download link
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `${filename}.csv`);
        link.style.visibility = "hidden";

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up URL object
        URL.revokeObjectURL(url);
      } else {
        console.error("CSV download not supported in this browser");
      }
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  }, []);

  /**
   * Generate and download CSV in one step
   * @param {Array} data - Array of objects to export
   * @param {Array} fields - Array of field names/headers
   * @param {string} filename - Filename for download
   * @param {Function} dataMapper - Optional function to transform data
   */
  const exportToCSV = useCallback(
    (data, fields, filename = "export", dataMapper = null) => {
      const csvContent = generateCSV(data, fields, dataMapper);
      if (csvContent) {
        downloadCSV(csvContent, filename);
      }
    },
    [generateCSV, downloadCSV],
  );

  /**
   * Generate filename with current date
   * @param {string} prefix - Filename prefix
   * @returns {string} Filename with date
   */
  const generateDateFilename = useCallback((prefix = "export") => {
    const today = new Date().toISOString().split("T")[0];
    return `${prefix}_${today}`;
  }, []);

  /**
   * Export data to XLSX format
   * @param {Array} data - Array of objects to export
   * @param {Array} fields - Array of field names/headers
   * @param {string} filename - Filename for download (without extension)
   * @param {Function} dataMapper - Optional function to transform data
   * @param {string} sheetName - Optional sheet name (default: "Sheet1")
   */
  const exportToXLSX = useCallback(
    (
      data,
      fields,
      filename = "export",
      dataMapper = null,
      sheetName = "Sheet1",
    ) => {
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.warn("No data provided for XLSX export");
        toast.warn("No data available for export");
        return;
      }

      try {
        // Transform data if mapper function is provided
        const processedData = dataMapper
          ? data.map((item, index) => dataMapper(item, index))
          : data;

        // Filter data to only include specified fields
        const filteredData = processedData.map((row) => {
          const filteredRow = {};
          fields.forEach((field) => {
            filteredRow[field] =
              row[field] !== undefined && row[field] !== null ? row[field] : "";
          });
          return filteredRow;
        });

        // Create a new workbook
        const workbook = XLSX.utils.book_new();

        // Convert data to worksheet
        const worksheet = XLSX.utils.json_to_sheet(filteredData, {
          header: fields,
        });

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Generate XLSX file and trigger download
        XLSX.writeFile(workbook, `${filename}.xlsx`);

        toast.success("Report downloaded successfully!");
      } catch (error) {
        console.error("Error exporting to XLSX:", error);
      }
    },
    [],
  );

  return {
    generateCSV,
    downloadCSV,
    exportToCSV,
    exportToXLSX,
    generateDateFilename,
  };
};
